import {
  menu,
  popMenu,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { PlusIcon } from '@blocksuite/icons/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { css } from '@emotion/css';
import { effect } from '@preact/signals-core';
import { property, query } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { styleMap } from 'lit/directives/style-map.js';
import { html } from 'lit/static-html.js';

import { renderUniLit } from '../../../../../../core';
import { createDndContext } from '../../../../../../core/utils/wc-dnd/dnd-context';
import { defaultActivators } from '../../../../../../core/utils/wc-dnd/sensors/index';
import { clampHorizontalToBounds } from '../../../../../../core/utils/wc-dnd/utils/horizontal-edge-scroll';
import { linearMove } from '../../../../../../core/utils/wc-dnd/utils/linear-move';
import { LEFT_TOOL_BAR_WIDTH } from '../../../../consts';
import { cellDivider } from '../../../../styles';
import type { VirtualTableViewUILogic } from '../../../table-view-ui-logic';
import * as styles from './column-header-css';
import { DataViewColumnPreview } from './column-move-preview';
import { getVerticalIndicator } from './vertical-indicator';
const leftBarStyle = css({
  width: LEFT_TOOL_BAR_WIDTH,
  flex: '0 0 auto',
});
type ColumnDropTarget = {
  before: boolean;
  id: string;
  left: number;
};

export class VirtualTableHeader extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  private readonly getHorizontalViewportBounds = () =>
    this.tableViewLogic.ui$.value?.getHorizontalViewportRect();

  private readonly dndContext = createDndContext({
    activators: defaultActivators,
    autoScroll: false,
    horizontalEdgeScroll: {
      bounds: this.getHorizontalViewportBounds,
      scrollBy: deltaX => {
        this.tableViewLogic.ui$.value?.scrollHorizontallyBy(deltaX);
      },
    },
    collisionDetection: linearMove(true),
    container: this,
    createOverlay: active => {
      if (this.readonly) {
        return;
      }
      const table = this.closest(
        'dv-table-view-ui-virtual'
      ) as HTMLElement | null;
      const container = table ?? this;
      const column = this.tableViewManager.propertyGetOrCreate(active.id);
      const preview = new DataViewColumnPreview();
      const viewportScale = Number.isFinite(this.dndContext.scale$.value.x)
        ? this.dndContext.scale$.value.x
        : 1;
      this.dndContext.scale$.value = { x: 1, y: 1 };
      preview.column = column;
      preview.container = container;
      preview.tableViewLogic = this.tableViewLogic;
      preview.viewportScale = viewportScale;
      preview.classList.add('data-view-drag-preview');
      preview.style.position = 'fixed';
      preview.style.zIndex = '999';
      preview.style.width = `${column.width$.value * viewportScale}px`;
      preview.style.top = `${active.rect.top}px`;
      preview.style.left = `${active.rect.left}px`;
      const cells = Array.from(
        container.querySelectorAll(`[data-column-id="${active.id}"]`)
      ) as HTMLElement[];
      cells.forEach(ele => {
        ele.style.opacity = '0.1';
      });
      document.body.append(preview);
      return {
        overlay: preview,
        cleanup: () => {
          preview.remove();
          cells.forEach(ele => {
            ele.style.opacity = '1';
          });
        },
      };
    },
    modifiers: [
      ({ transform }) => {
        return {
          ...transform,
          y: 0,
        };
      },
      clampHorizontalToBounds(this.getHorizontalViewportBounds),
    ],
    onDragEnd: ({ active, delta }) => {
      if (this.readonly) {
        return;
      }
      const target = this.resolveColumnDropTarget(
        active.id,
        active.rect,
        delta
      );
      if (!target || target.id === active.id) {
        return;
      }
      this.tableViewManager.propertyGetOrCreate(active.id).move({
        before: target.before,
        id: target.id,
      });
    },
  });

  private resolveColumnDropTarget(
    activeId: string,
    activeRect: { left: number; width: number },
    delta: { x: number }
  ): ColumnDropTarget | undefined {
    const columns = this.tableViewLogic.layoutSnapshot$.value.dataColumns.map(
      column => column.property
    );
    const activeIndex = columns.findIndex(column => column.id === activeId);
    if (activeIndex < 0 || columns.length <= 1) {
      return;
    }

    const activeCenter = activeRect.left + activeRect.width / 2;
    const movedLeft = activeRect.left + delta.x;
    const movedRight = movedLeft + activeRect.width;
    const remaining = columns
      .filter(column => column.id !== activeId)
      .map(column => {
        const element = this.querySelector<HTMLElement>(
          `virtual-database-header-column[data-column-id="${column.id}"]`
        );
        return element
          ? {
              column,
              rect: element.getBoundingClientRect(),
            }
          : undefined;
      })
      .filter((entry): entry is NonNullable<typeof entry> => !!entry);

    if (remaining.length === 0) {
      return;
    }

    if (delta.x > 0) {
      const crossed = remaining.filter(({ rect }) => {
        const center = rect.left + rect.width / 2;
        return center > activeCenter && movedRight > center;
      });
      const target = crossed.at(-1);
      if (!target) {
        return;
      }
      return {
        before: false,
        id: target.column.id,
        left: target.rect.right,
      };
    }

    if (delta.x >= 0) {
      return;
    }

    const target = remaining.find(({ rect }) => {
      const center = rect.left + rect.width / 2;
      return center < activeCenter && movedLeft < center;
    });
    if (!target) {
      return;
    }
    return {
      before: true,
      id: target.column.id,
      left: target.rect.left,
    };
  }

  private readonly _onAddColumn = (e: MouseEvent) => {
    if (this.readonly) return;
    const ele = e.currentTarget as HTMLElement;
    popMenu(popupTargetFromElement(ele), {
      options: {
        preferDesktop: true,
        title: {
          text: 'Property type',
        },
        items: [
          menu.group({
            items: this.tableViewManager.propertyMetas$.value.map(config => {
              return menu.action({
                name: config.config.name,
                prefix: renderUniLit(config.renderer.icon),
                select: () => {
                  const id = this.tableViewManager.propertyAdd('end', {
                    type: config.type,
                    name: config.config.name,
                  });
                  if (id) {
                    requestAnimationFrame(() => {
                      ele.scrollIntoView({
                        block: 'nearest',
                        inline: 'nearest',
                      });
                      this.openPropertyMenuById(id);
                    });
                  }
                },
              });
            }),
          }),
        ],
      },
    });
  };

  openPropertyMenuById = (id: string) => {
    const column = this.querySelectorAll('virtual-database-header-column');
    for (const item of column) {
      if (item.dataset.columnId === id) {
        item.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        item.editTitle();
        return;
      }
    }
  };

  private get readonly() {
    return this.tableViewManager.readonly$.value;
  }

  private readonly showColumnMoveIndicator = () => {
    const columnMoveIndicator = getVerticalIndicator();
    this.disposables.add(
      effect(() => {
        const active = this.dndContext.active$.value;
        if (!active) {
          columnMoveIndicator.remove();
          return;
        }
        // Edge auto-scroll moves the header columns under a resting pointer;
        // reading the scroll position keeps the indicator in sync with them.
        this.tableViewLogic.ui$.value?.xScrollLeft$.value;
        const target = this.resolveColumnDropTarget(
          active.id,
          active.rect,
          this.dndContext.transform$.value
        );
        if (!target) {
          columnMoveIndicator.remove();
          return;
        }
        const header = this.getBoundingClientRect();
        columnMoveIndicator.display(target.left, header.top, header.height);
      })
    );
  };

  override connectedCallback() {
    super.connectedCallback();
    this.classList.add(styles.columnHeaderContainer);
    this.showColumnMoveIndicator();
  }

  override render() {
    return html`
      <div class="${styles.columnHeader} database-row">
        <div class="${leftBarStyle}"></div>
        ${repeat(
          this.tableViewLogic.layoutSnapshot$.value.dataColumns,
          column => column.id,
          ({ property: column, width }, index) => {
            const style = styleMap({
              width: `${width}px`,
              border: index === 0 ? 'none' : undefined,
            });
            return html`
              <virtual-database-header-column
                style="${style}"
                data-column-id="${column.id}"
                data-column-index="${index}"
                class="${styles.column} ${styles.cell}"
                .column="${column}"
                .tableViewLogic="${this.tableViewLogic}"
              ></virtual-database-header-column>
              <div class="${cellDivider}" style="height: auto;"></div>
            `;
          }
        )}
        <div
          @click="${this._onAddColumn}"
          class="${styles.headerAddColumnButton}"
          data-testid="database-add-column"
        >
          ${PlusIcon()}
        </div>
        <div class="scale-div" style="width: 1px;height: 1px;"></div>
      </div>
    `;
  }

  @query('.scale-div')
  accessor scaleDiv!: HTMLDivElement;

  @property({ attribute: false })
  accessor tableViewLogic!: VirtualTableViewUILogic;

  get tableViewManager() {
    return this.tableViewLogic.view;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'virtual-table-header': VirtualTableHeader;
  }
}
